import { Fragment } from 'react';
import { AdSlot } from 'components/UI/AdSlot';
import { getAdType } from 'components/UI/AdSlot/AdSlotHelper';
import { Teaser } from 'components/UI/Teaser';
import { AdSlotType, type AdsProps, type AdsPropsInList } from 'types/ads';
import type { WidgetTeaserItemProps, WidgetTeaserStandardProps } from 'types/cms-teaser';
import type { WidgetSeriesProps } from 'types/series';
import { generateWeakKey } from 'utils/idGenerator';
import { isRtlPlusType } from 'utils/rtlPlus';
import { slugify } from 'utils/string';
import type { TeaserVariant } from '../Teaser/Teaser';
import TeaserSearch from '../TeaserSearch/TeaserSearch';
import styles from './AdInsideList.module.scss';

export type PlayableAdsProps = {
  [positionInList: number | string]: AdItemProp;
};

export type AdItemProp = {
  [type: string]: AdsProps;
};

type PlayableItem = {
  readonly isAd?: boolean;
  readonly teaserItem?: WidgetTeaserItemProps;
  readonly adItem?: AdItemProp;
};

/*
Search for ads in models
Handling the aps-position to simplify editing in site-manager
*/
export const findPlayableAds = (
  context: WidgetTeaserStandardProps | WidgetSeriesProps,
  skipPos?: boolean
): PlayableAdsProps => {
  const ads: PlayableAdsProps = {};

  const decreaseIndex = {
    [`${AdSlotType.Desktop}`]: 0,
    [`${AdSlotType.Mobile}`]: 0,
  };

  context.model?.fields?.adSlots?.forEach((ad: AdsPropsInList) => {
    const type = getAdType(ad.name);
    let pos = ad.positionInList;
    if (!skipPos) {
      pos += decreaseIndex[type];
    }

    if (!ads[pos]) {
      ads[pos] = {};
    }

    ads[pos][type] = {
      name: ad.name,
      position: ad.position,
      outbrainWidgetId: ad.outbrainWidgetId,
    };

    decreaseIndex[type] -= 1;
  });

  return ads;
};

export const rearrangeItemsList = (context: WidgetTeaserStandardProps, offset: number = 0): Array<PlayableItem> => {
  if (!context?.items || context.items.length === 0) {
    return [];
  }

  const ads = findPlayableAds(context);
  const items: Array<PlayableItem> = [];

  context.items.forEach((item, index) => {
    if (ads[index + offset + 1]) {
      items.push({ isAd: true, adItem: ads[index + offset + 1] });
    }
    items.push({ teaserItem: item });
  });

  if (ads[context.items.length + offset + 1]) {
    items.push({ isAd: true, adItem: ads[context.items.length + offset + 1] });
  }

  return items;
};

export const getViewport = (name: string) => {
  switch (name) {
    case 'mobile':
    case 'outbrain-mobile':
    case 'dmofooter':
      return AdSlotType.Mobile;
    case 'billboard':
    case 'outbrain':
    case 'rectangle':
    case 'superbanner':
      return AdSlotType.Desktop;
    default:
      return AdSlotType.Default;
  }
};

export const RenderAd = ({ el }: { el: AdItemProp }) => {
  return (
    <>
      {Object.entries(el).map(([type, ad]) => (
        <AdSlot
          key={`adSlot-${type}-${ad.name}-${ad.position}`}
          name={ad.name}
          className={styles.adslot}
          position={ad.position}
          outbrainWidgetId={ad.outbrainWidgetId}
          viewport={getViewport(ad.name)}
          skipPrediction
        />
      ))}
    </>
  );
};

export const RenderTeaser = ({
  teaser,
  sectionTitle,
  index,
  teaserOffset = 0,
  sectionSlug,
  teaserImageWidth,
  leadTeaserImageWidth,
  firstTeaserHighlight,
  variant,
  disableLazyLoading = false,
}: {
  teaser: WidgetTeaserItemProps;
  sectionTitle: string;
  index: number;
  teaserOffset?: number;
  sectionSlug?: string;
  teaserImageWidth?: number;
  leadTeaserImageWidth?: number;
  firstTeaserHighlight?: boolean;
  variant?: TeaserVariant;
  disableLazyLoading?: boolean;
}) => {
  const slug = sectionSlug ?? slugify(sectionTitle);
  const id = `${slug}-teaser${teaserOffset + index + 1}`;

  if (variant === 'search') {
    return <TeaserSearch id={id} teaser={teaser} />;
  }

  const isLeadImage = leadTeaserImageWidth && index === 0;
  const isRtlPlus = isRtlPlusType(teaser.type);
  const isGallery = teaser.type === 'gallery';
  const link = isRtlPlus
    ? (teaser.model?.fields?.url ?? teaser.model.url)
    : (teaser.model?.fields?.externalUrl ?? teaser.model.url);

  return (
    <Teaser
      id={id}
      title={teaser.model.fields['teaser-title'] ?? teaser.model.fields.title}
      subtitle={teaser.model.themenwelt?.section_name}
      image={{
        image: teaser.model.image,
        width: isLeadImage ? leadTeaserImageWidth : teaserImageWidth,
        alt: teaser.model?.image?.fields?.alt?.value
          ? teaser.model.image?.fields.alt.value
          : `Bild zu: "${teaser.model.fields.title}"`,
      }}
      linkHref={link}
      variant={isLeadImage ? 'large' : 'medium'}
      subtitleColor={teaser.model.themenwelt?.secondary_color}
      isHighlight={firstTeaserHighlight && index === 0}
      isVideo={!!teaser.model.fields.encoding?.[0]?.value?.duration?.value}
      videoDuration={Number(teaser.model.fields.encoding?.[0]?.value?.duration?.value)}
      isSponsored={teaser.type === 'sponsored-article'}
      isRtlPlus={isRtlPlus}
      openLinkInNewTab={teaser.model.fields?.open_new_window === 'true'}
      isGallery={isGallery}
      galleryCount={teaser.model.galleryCount ?? undefined}
      disableLazyLoading={disableLazyLoading}
    />
  );
};

export const AdInsideList = ({
  context,
  teaserImageWidth,
  leadTeaserImageWidth,
  firstTeaserHighlight = false,
  offset = 0,
  teaserOffset = 0,
  sectionSlug,
  variant,
  disableLazyLoading = false,
}: {
  context: WidgetTeaserStandardProps;
  teaserImageWidth?: number;
  leadTeaserImageWidth?: number;
  firstTeaserHighlight?: boolean;
  offset?: number;
  teaserOffset?: number;
  sectionSlug?: string;
  variant?: TeaserVariant;
  disableLazyLoading?: boolean;
}) => {
  const items = rearrangeItemsList(context, offset);
  let teaserCounter = 0;

  return (
    <>
      {items.map((el) => (
        <Fragment key={`${generateWeakKey(el)}`}>
          {el.adItem && <RenderAd el={el.adItem} />}
          {el.teaserItem && (
            <RenderTeaser
              teaser={el.teaserItem}
              sectionTitle={context.model?.fields?.title ?? context.view ?? ''}
              index={teaserCounter++}
              teaserOffset={teaserOffset}
              sectionSlug={sectionSlug}
              teaserImageWidth={teaserImageWidth}
              leadTeaserImageWidth={leadTeaserImageWidth}
              firstTeaserHighlight={firstTeaserHighlight}
              variant={variant}
              disableLazyLoading={teaserCounter < 2 ? disableLazyLoading : false}
            />
          )}
        </Fragment>
      ))}
    </>
  );
};
